// Signup.tsx
// Page de création de compte Qollect.
// Updated: suppression des placeholders, correction couleur liens (primary-600),
// taille bouton CTA passée en md, prop loading du Button, titre en font-bold.
// Updated: remplacement des champs Input mot de passe par PasswordInput avec toggle Eye/EyeOff.
// Updated: guarded console.error to dev-only to avoid leaking internal error details in production.
// Updated: ajout PasswordStrengthIndicator, validation inline mismatch temps réel, seuil min 8 caractères.
// Updated: ajout des champs Prénom et Nom (obligatoires) avant l'email.
//   Transmis via options.data lors du signUp pour persistance dans public.users
//   via le trigger handle_new_user.
// Updated 2026-04-07: remplacement du titre texte "Qollect" par le logo SVG.

import { useState, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import PasswordInput from '../components/ui/PasswordInput';
import PasswordStrengthIndicator from '../components/ui/PasswordStrengthIndicator';
import Card from '../components/ui/Card';

export default function Signup() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signUp } = useAuth();
  const navigate = useNavigate();

  const isWeak = password.length > 0 && password.length < 8;
  const mismatch = confirmPassword.length > 0 && password !== confirmPassword;

  const handleConfirmChange = (value: string) => {
    setConfirmPassword(value);
    if (value.length > 0 && password !== value) {
      setConfirmError('Les mots de passe ne correspondent pas');
    } else {
      setConfirmError('');
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!firstName.trim() || !lastName.trim() || !email || !password || !confirmPassword) {
      setError('Veuillez remplir tous les champs');
      setLoading(false);
      return;
    }

    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      setLoading(false);
      return;
    }

    const { error: signUpError } = await signUp(email, password, firstName.trim(), lastName.trim());

    if (signUpError) {
      if (import.meta.env.DEV) {
        console.error('Signup error:', signUpError);
      }

      if (signUpError.message.includes('already registered')) {
        setError('Cet email est déjà utilisé');
      } else if (signUpError.message.includes('email_address_invalid') || signUpError.message.includes('invalid')) {
        setError('Format d\'email invalide. Veuillez utiliser une adresse email valide (ex: exemple@gmail.com)');
      } else if (signUpError.message.includes('password')) {
        setError('Le mot de passe ne respecte pas les critères de sécurité');
      } else {
        setError(`Erreur: ${signUpError.message}`);
      }
      setLoading(false);
      return;
    }

    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-neutral-100 flex items-center justify-center px-4 py-8">
      <Card className="w-full max-w-md shadow-soft-xl">
        <div className="text-center mb-10">
          <img src="/logo-qollect.svg" alt="Qollect" className="h-[50px] w-auto mx-auto mb-4" />
          <p className="text-neutral-500 text-lg">Créez votre compte professionnel</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-4 rounded-xl font-medium">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Prénom"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
              required
            />
            <Input
              label="Nom"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
              required
            />
          </div>

          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />

          <div>
            <PasswordInput
              label="Mot de passe"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
            <PasswordStrengthIndicator password={password} />
            {isWeak && (
              <p className="mt-1.5 text-xs text-error-600 font-medium">
                Le mot de passe doit contenir au moins 8 caractères
              </p>
            )}
          </div>

          <PasswordInput
            label="Confirmer le mot de passe"
            value={confirmPassword}
            onChange={(e) => handleConfirmChange(e.target.value)}
            autoComplete="new-password"
            required
            error={mismatch ? confirmError : undefined}
          />

          <Button
            type="submit"
            fullWidth
            size="md"
            loading={loading}
          >
            Créer mon compte
          </Button>
        </form>

        <div className="mt-8 text-center">
          <p className="text-neutral-600">
            Vous avez déjà un compte ?{' '}
            <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium transition-colors">
              Se connecter
            </Link>
          </p>
        </div>
      </Card>
    </div>
  );
}
